import onetime from "@-/com.npmjs.onetime";
import { useSyncExternalStore } from "react";

type IdentityState = { distinctId: string | null; listeners: Set<() => void> };

const getIdentityState = onetime(
  (): IdentityState => ({ distinctId: null, listeners: new Set() }),
);

/**
 * Called by `lib/posthog` once an identity change has settled (after `reset`/`identify`). Super
 * properties and integration attributes are re-applied by subscribers, because `reset()` clears
 * them.
 */
export function publishAnalyticsDistinctId(distinctId: string): void {
  const state = getIdentityState();
  if (state.distinctId === distinctId) {
    return;
  }
  state.distinctId = distinctId;
  for (const listener of state.listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  const { listeners } = getIdentityState();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): string | null {
  return getIdentityState().distinctId;
}

/** The settled PostHog distinct id, or `null` before identity has resolved this runtime. */
export function useAnalyticsDistinctId(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
